import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Progress,
  List,
  message,
  Modal,
  Button,
  Tooltip,
} from "antd";
import { UploadOutlined, DeleteOutlined, FolderOpenOutlined, FileAddOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { fetchAwsCredentials } from "./uploadApi";
import { S3Client } from "@aws-sdk/client-s3";
import { Upload as AwsUpload } from "@aws-sdk/lib-storage";
import { useMsal } from "@azure/msal-react";
import { useDropzone } from "react-dropzone";
import { IGetAgCredsResponse, getAgCreds } from "components/ObjectList/objectListApi";
import "./style.scss";

export interface UploadComponentProps {
  spaceName: string;
  selectedSpaceRegion: any;
  path: string;
  onUploadComplete?: () => void;
  agCredsResponseData: IGetAgCredsResponse | boolean;
  dataAccountId: string;
  spaceDisplayName: string;
  domainName?: string;
  uploaderEmail?: string;
}

export interface UploadHandle {
  open: () => void;
}

interface FileWithProgress {
  uid: string;
  file: File;
  percent: number;
  status: "pending" | "uploading" | "done" | "error";
  relativePath?: string;
}

interface FileSystemEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
}

interface FileSystemFileEntry extends FileSystemEntry {
  file: (callback: (file: File) => void) => void;
}

interface FileSystemDirectoryEntry extends FileSystemEntry {
  createReader: () => {
    readEntries: (callback: (entries: FileSystemEntry[]) => void) => void;
  };
}

const UploadComponent = forwardRef<UploadHandle, UploadComponentProps>(
  ({ spaceName, selectedSpaceRegion, path, onUploadComplete, agCredsResponseData, dataAccountId, spaceDisplayName, domainName, uploaderEmail }, ref) => {
    const [fileList, setFileList] = useState<FileWithProgress[]>([]);
    const [uploading, setUploading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const msalInstance = useMsal();
    const s3ClientRef = useRef<S3Client | null>(null);

    const folderInputRef = useRef<HTMLInputElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(ref, () => ({
      open: () => setModalVisible(true),
    }));

    useEffect(() => {
      (async () => {
        try {
          let creds;
          if (
            agCredsResponseData &&
            typeof agCredsResponseData === "object" &&
            "aws_access_key_id" in agCredsResponseData &&
            "aws_secret_access_key" in agCredsResponseData &&
            "aws_session_token" in agCredsResponseData
          ) {
            creds = {
              accessKeyId: agCredsResponseData.aws_access_key_id,
              secretAccessKey: agCredsResponseData.aws_secret_access_key,
              sessionToken: agCredsResponseData.aws_session_token,
              region: selectedSpaceRegion || "us-east-1",
            };
          } else {
            creds = await fetchAwsCredentials(msalInstance, spaceName);
          }
          if (creds) {
            s3ClientRef.current = new S3Client({
              region: selectedSpaceRegion || creds.region,
              credentials: {
                accessKeyId: creds.accessKeyId,
                secretAccessKey: creds.secretAccessKey,
                sessionToken: creds.sessionToken,
              },
            });
          }
        } catch (err) {
          console.error("Error fetching AWS credentials", err);
        }
      })();
    }, [msalInstance, spaceName, agCredsResponseData, selectedSpaceRegion]);

    useEffect(() => {
      if (!modalVisible) {
        setFileList([]);
      }
    }, [modalVisible]);

    // Traverse dropped folder
    const traverseFileTree = async (
      item: FileSystemEntry,
      path = "",
    ): Promise<FileWithProgress[]> => {
      return new Promise((resolve) => {
        if (item.isFile) {
          (item as FileSystemFileEntry).file((file: File) => {
            resolve([{
              uid: `${file.name}-${Date.now()}`,
              file,
              percent: 0,
              status: "pending",
              relativePath: path + file.name,
            }]);
          });
        } else if (item.isDirectory) {
          const dirReader = (item as FileSystemDirectoryEntry).createReader();
          dirReader.readEntries(async (entries) => {
            const nestedFiles: FileWithProgress[] = [];
            for (const entry of entries) {
              const subFiles = await traverseFileTree(entry, path + (item as FileSystemDirectoryEntry).name + "/");
              nestedFiles.push(...subFiles);
            }
            resolve(nestedFiles);
          });
        } else {
          resolve([]);
        }
      });
    };

    // Custom onDrop handler
    const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const items = event.dataTransfer.items;
      const allFiles: FileWithProgress[] = [];

      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) {
          const files = await traverseFileTree(entry);
          allFiles.push(...files);
        }
      }

      if (allFiles.length === 0) {
        message.error("Cannot upload an empty folder. Please select a folder with files.");
        return;
      }

      setFileList((prev) => [...prev, ...allFiles]);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      // onDrop,
      noClick: true,
      noKeyboard: true,
      multiple: true,
    });

    const handleRemove = (file: FileWithProgress) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    };

    const uploadFile = async (item: FileWithProgress) => {
      const { file, uid } = item;
      if (!s3ClientRef.current) {
        message.error("S3 Client not initialized yet");
        return;
      }

      const safePath = (item.relativePath || file.name)
        .replace(/\\/g, "/")        // Normalize Windows paths
        .replace(/\s+/g, "_");      // Replace spaces (optional)

      const fullKey = `${path.endsWith("/") ? path : path + ""}${safePath}`;
      const params = {
        Bucket: spaceName,
        Key: `${fullKey}`,
        Body: file,
        // Explicitly disable checksum algorithm for multipart upload
        ChecksumAlgorithm: undefined,
      };


      const parallel = new AwsUpload({
        client: s3ClientRef.current,
        params,
        leavePartsOnError: false,
        partSize: 5 * 1024 * 1024 * 1024, // 5GB part size to avoid multipart for files ≤ 5GB
      });

      parallel.on("httpUploadProgress", (prog: any) => {
        if (prog.total) {
          const pct = Math.round((prog.loaded / prog.total) * 100);
          setFileList((prev) =>
            prev.map((f) =>
              f.uid === uid ? { ...f, percent: pct, status: "uploading" } : f,
            ),
          );
        }
      });

      try {
        await parallel.done();
        setFileList((prev) =>
          prev.map((f) =>
            f.uid === uid ? { ...f, percent: 100, status: "done" } : f,
          ),
        );
        message.success(`${file.name} uploaded`);
      } catch (err) {
        console.error(err);
        setFileList((prev) =>
          prev.map((f) =>
            f.uid === uid ? { ...f, status: "error" } : f,
          ),
        );
        message.error(`${file.name} failed`);
      }
    };

    const handleUpload = async () => {
      // Fetch fresh credentials before uploading
      let freshAgCreds: IGetAgCredsResponse | boolean;
      try {
        freshAgCreds = await getAgCreds(msalInstance, {
          bucket_name: spaceName,
          region: selectedSpaceRegion,
          data_account_id: dataAccountId,
          space_name: spaceDisplayName,
          access_level: "READWRITE",
        });
        
        if (freshAgCreds === false) {
          message.error("Failed to get access credentials. Please try again later.");
          return;
        }

        // Update S3 client with fresh credentials
        if (typeof freshAgCreds === "object" && "aws_access_key_id" in freshAgCreds) {
          s3ClientRef.current = new S3Client({
            region: selectedSpaceRegion || "us-east-1",
            credentials: {
              accessKeyId: freshAgCreds.aws_access_key_id,
              secretAccessKey: freshAgCreds.aws_secret_access_key,
              sessionToken: freshAgCreds.aws_session_token,
            },
          });
        }
      } catch (err: any) {
        console.error("Error fetching credentials:", err);
        message.error("Failed to get access credentials. Please try again later.");
        return;
      }

      setUploading(true);
      const pending = fileList.filter((f) => f.status === "pending");
      await Promise.all(pending.map(uploadFile));

      setTimeout(() => {
        setUploading(false);
        onUploadComplete?.();
        setModalVisible(false);
      }, 300);
    };

    const handleFiles = (files: FileList, useRelative = false) => {
      const newFiles: FileWithProgress[] = Array.from(files).map((file) => ({
        uid: `${file.name}-${Date.now()}`,
        file,
        percent: 0,
        status: "pending",
        relativePath: useRelative
          ? (file as any).webkitRelativePath || file.name
          : file.name,
      }));
      setFileList((prev) => [...prev, ...newFiles]);
    };


    return (
      <Modal
        title="Upload Files"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <Button
            icon={<FileAddOutlined />}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload Files
          </Button>
          <Tooltip title="Note: Empty folders cannot be uploaded. Please select a folder with files.">
            <Button
              icon={<FolderOpenOutlined />}
              onClick={() => folderInputRef.current?.click()}
            >
              Upload Folder
            </Button>
          </Tooltip>
        </div>

        <input
          type="file"
          multiple
          hidden
          ref={fileInputRef}
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
        <input
          type="file"
          multiple
          hidden
          ref={folderInputRef}
          onChange={(e) => {
            const files = e.target.files;
            if (!files || files.length === 0) {
              Modal.confirm({
                title: "You are uploading 0 files",
                content: "Click Upload to continue.",
                okText: "Upload",
                cancelText: "Cancel",
                onOk: () => {
                  message.error("Cannot upload an empty folder. Please select a folder with files.");
                },
              });
              return;
            }
            handleFiles(files, true);
          }}
          {...({ webkitdirectory: "", directory: true } as any)}
        />

        <div
          {...getRootProps()}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          style={{
            border: "1px dashed #ccc",
            borderRadius: 8,
            padding: 24,
            textAlign: "center",
            background: isDragActive ? "#fafafa" : "#fff",
            marginBottom: 24,
          }}
        >
          <input
            {...getInputProps()}
            {...({ webkitdirectory: "", mozdirectory: "", directory: true } as any)}
          />
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Drag and Drop folders/files here to upload</p>
          <p className="ant-upload-hint">Supports nested folder structure (Chrome/Edge)</p>
        </div>

        <List
          dataSource={fileList}
          className="file-list-container"
          renderItem={(item) => (
            <List.Item
              key={item.uid}
            >
              <div style={{ width: "100%" }}>
                {/* TOP SECTION: Name + File Info */}
                <div style={{ marginBottom: 8 }}>
                  <strong
                    style={{
                      wordBreak: "break-all",
                    }}
                  >
                    {item.relativePath || item.file.name}
                  </strong>

                  <div style={{ fontSize: 12, color: "#888" }}>
                    {item.file.type || "unknown"} — {(item.file.size / 1024).toFixed(1)} KB
                  </div>
                </div>

                {/* BOTTOM SECTION: Progress + Delete Icon in same row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <Progress
                    percent={item.percent}
                    status={item.status === "error" ? "exception" : undefined}
                    size="small"
                    style={{ flex: 1 }}
                  />

                  <DeleteOutlined
                    style={{ color: "#ff4d4f", fontSize: 18, cursor: "pointer" }}
                    onClick={() => handleRemove(item)}
                    title="Remove"
                  />
                </div>
              </div>
            </List.Item>
          )}
        />

        {/* Show file count if there are files */}
        {fileList.length > 0 && (
          <div className="file-count-text">
            {fileList.length === 1 ? "1 file selected" : `${fileList.length} files selected`}
          </div>
        )}

        <Button
          type="primary"
          onClick={handleUpload}
          disabled={!fileList.length || uploading}
          loading={uploading}
          block
          style={{ marginTop: 16 }}
        >
          {uploading ? "Uploading..." : "Start Upload"}
        </Button>
      </Modal>
    );
  },
);

export default UploadComponent;
